'use client';

import { createContext, useContext, type ReactNode } from 'react';

/**
 * Event-wide facts the server layout reads once and hands to `EventLayout`, so the tab bar is
 * right from the first paint instead of popping in after client fetches.
 * - `venueCount`: listed venues (`event_venue` rows); 0 = no Venues tab.
 * - `hasMerchandise`: something is on sale; false = no Merchandise tab.
 * - `hasFeeds`: the event has court streams; false/absent = no Watch tab.
 */
type EventShell = {
  venueCount: number;
  hasMerchandise: boolean;
  hasFeeds?: boolean;
  feedsLive?: boolean;
};

const EventShellContext = createContext<EventShell>({
  venueCount: 0,
  hasMerchandise: false,
  hasFeeds: false,
  feedsLive: false,
});

export function EventShellProvider({
  venueCount,
  hasMerchandise,
  hasFeeds = false,
  feedsLive = false,
  children,
}: EventShell & { children: ReactNode }) {
  return (
    <EventShellContext.Provider value={{ venueCount, hasMerchandise, hasFeeds, feedsLive }}>
      {children}
    </EventShellContext.Provider>
  );
}

export function useEventVenueCount(): number {
  return useContext(EventShellContext).venueCount;
}

export function useEventHasMerchandise(): boolean {
  return useContext(EventShellContext).hasMerchandise;
}

export function useEventHasFeeds(): boolean {
  return useContext(EventShellContext).hasFeeds === true;
}

/** An app stream is live now (drives the Watch tab's live marker). */
export function useEventFeedsLive(): boolean {
  return useContext(EventShellContext).feedsLive === true;
}
